import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const APP_NAME = 'SLACC';
const SITE_NAME = 'Sindh Legal Advisory Call Center';
const DEFAULT_DESCRIPTION =
  'SLACC is the web platform for Sindh Legal Advisory Call Center, supporting legal aid call handling, caller intake, front-office operations, and back-office review workflows.';
const DEFAULT_KEYWORDS =
  'SLACC, Sindh Legal Advisory Call Center, legal aid, call center, Legal Aid Society Pakistan, front office, back office, legal advisory, case intake';
const DEFAULT_IMAGE_PATH = '/social-preview.png';
const INDEXABLE_ROBOTS = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';
const PRIVATE_ROBOTS = 'noindex,nofollow,noarchive';

const ROUTE_META = {
  '/': {
    title: 'SLACC Login',
    description:
      'Access the SLACC platform for legal aid call handling, caller intake, call routing, and case workflow management.',
    robots: INDEXABLE_ROBOTS
  },
  '/login': {
    title: 'SLACC Login',
    description:
      'Access the SLACC platform for legal aid call handling, caller intake, call routing, and case workflow management.',
    robots: INDEXABLE_ROBOTS
  },
  '/frontoffice': {
    title: 'Front Office',
    description:
      'Manage incoming legal aid calls, caller records, notes, reminders, and intake workflows in the SLACC front office.',
    robots: PRIVATE_ROBOTS
  },
  '/frontoffice-new': {
    title: 'Front Office Workspace',
    description:
      'Use the modern SLACC front office workspace to handle legal aid calls, caller details, transfers, and live operational workflows.',
    robots: PRIVATE_ROBOTS
  },
  '/frontoffice-shortcuts': {
    title: 'Front Office Shortcut Guide',
    description:
      'Reference the keyboard shortcuts and workflow accelerators available inside the SLACC front office interface.',
    robots: PRIVATE_ROBOTS
  },
  '/backoffice': {
    title: 'Back Office',
    description:
      'Review approvals, dashboards, departmental queues, and legal aid case workflows in the SLACC back office.',
    robots: PRIVATE_ROBOTS
  },
  '/logout': {
    title: 'Logout',
    description: 'Securely sign out of the SLACC platform.',
    robots: PRIVATE_ROBOTS
  }
};

function upsertMeta(attributeName, attributeValue, content) {
  let element = document.head.querySelector(`meta[${attributeName}="${attributeValue}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

function resolveAssetUrl(pathname) {
  const publicUrl = process.env.PUBLIC_URL || '';
  const normalizedPublicUrl = publicUrl.endsWith('/') ? publicUrl.slice(0, -1) : publicUrl;
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;

  return new URL(`${normalizedPublicUrl}${normalizedPath}`, window.location.origin).toString();
}

function resolveCanonicalUrl(pathname) {
  const publicUrl = process.env.PUBLIC_URL || '';

  if (/^https?:\/\//i.test(publicUrl)) {
    return new URL(pathname, publicUrl.endsWith('/') ? publicUrl : `${publicUrl}/`).toString();
  }

  const basePath = publicUrl ? `/${publicUrl.replace(/^\/+|\/+$/g, '')}` : '';
  return new URL(`${basePath}${pathname}`, window.location.origin).toString();
}

function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const routeMeta = ROUTE_META[location.pathname] || {
      title: `${APP_NAME} | ${SITE_NAME}`,
      description: DEFAULT_DESCRIPTION,
      robots: PRIVATE_ROBOTS
    };

    const title = routeMeta.title.includes(APP_NAME)
      ? routeMeta.title
      : `${routeMeta.title} | ${APP_NAME}`;
    const description = routeMeta.description || DEFAULT_DESCRIPTION;
    const robots = routeMeta.robots || PRIVATE_ROBOTS;
    const canonicalUrl = resolveCanonicalUrl(location.pathname || '/');
    const imageUrl = resolveAssetUrl(DEFAULT_IMAGE_PATH);

    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', DEFAULT_KEYWORDS);
    upsertMeta('name', 'robots', robots);
    upsertMeta('name', 'googlebot', robots);

    upsertMeta('property', 'og:locale', 'en_PK');
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', APP_NAME);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:image', imageUrl);
    upsertMeta('property', 'og:image:alt', 'SLACC social preview');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', imageUrl);

    upsertLink('canonical', canonicalUrl);
  }, [location.pathname]);

  return null;
}

export default SeoManager;
